import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import AppEnv from '@/config/config';
import { useBambooTopUpTransactionMutation } from '@/gql/generated';
import { InvalidInputError } from '@/lib/error-type';
import { handleError } from '@/lib/handle-error';
import { formatDateToTimezone } from '@/lib/timezone';
import { calculateDiscountPercent, formatNumberToCurrency } from '@/lib/utils';
import { FullScreenLoadingContext } from '@/pages/_providers/full-loading-overlay-provider';
import { useAuthenticatedProvider } from '@/pages/(gapsdk)/(authenticated)/_provider/authenticated-provider';
import { formatVisaExpiry, paymentMethods } from '@/utils/constant';
import { createTEConnect } from '@magensa/te-connect';
import { TeConnectJs } from '@magensa/te-connect-js';
import { TEConnect } from '@magensa/te-connect-react';
import creditCardType from 'credit-card-type';
import bigDecimal from 'js-big-decimal';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import TopNavigation from '../../../_components/top-navigation';
import PaymentFailed from './_components/card-entry-failded';
import PaymentSuccess from './_components/card-entry-successful';

type CreatePaymentMagensaRespone = {
  token: string;
  cardMetaData: {
    expirationDate: string;
    maskedPAN: string;
    billingZip: string;
  };
};

export default function CardEntryAddFundPage() {
  const magensaContainerId = 'magensa-container';
  const authHeader = AppEnv.get().magensaAuthHeader;
  const teConnectInstance = createTEConnect(authHeader);
  const navigate = useNavigate();
  const { event: eventLoading } = useContext(FullScreenLoadingContext);
  const { user } = useAuthenticatedProvider();
  const { code } = useParams();

  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const balanceChange = searchParams.get('balance_change');
  const price = searchParams.get('price');
  const discountPercentValue = calculateDiscountPercent(
    Number(balanceChange),
    Number(price),
  );
  const [error, setError] = useState<string>('');

  const bambooTopUpTransactionMutation = useBambooTopUpTransactionMutation({
    onMutate: () => {
      eventLoading.setIsLoading(true);
    },
    onSuccess: () => {
      eventLoading.setIsLoading(false);
    },
    // eslint-disable-next-line @typescript-eslint/no-shadow
    onError: (error) => {
      const errorMessage = handleError({ error });
      if (
        errorMessage instanceof InvalidInputError &&
        errorMessage.errors.length > 0
      ) {
        setError(errorMessage.errors[0]?.message ?? 'Something went wrong');
      }
    },
    onSettled: () => {
      eventLoading.setIsLoading(false);
    },
  });

  const [teConnectRef, setTeConnectRef] = useState<{
    createPayment: () => CreatePaymentMagensaRespone;
  } | null>(null);

  const mountCardIframe = useCallback(() => {
    const teInstance = createTEConnect(authHeader);
    const teConnect = new TeConnectJs(teInstance);
    const style = getComputedStyle(document.body);
    const elm = document.getElementById('element_4_compute');
    if (elm) {
      elm.style.borderRadius = `calc(${style.getPropertyValue(
        '--radius',
      )} - 2px)`;
      const borderRadius = parseInt(
        window?.getComputedStyle(elm).borderRadius.slice(0, -2),
        10,
      );

      const cardStyles = {
        base: {
          wrapper: {
            margin: '0px -8px',
            padding: '0px',
            direction: 'column',
            flexWrap: 'wrap',
          },
          variants: {
            inputType: 'outlined', // 'outlined', 'filled', 'standard'
            inputMargin: 'dense',
            autoMinHeight: false,
          },
          backgroundColor: `white`,
        },
        boxes: {
          labelColor: 'rgb(161, 161, 170)',
          textColor: '#000000',
          borderRadius: borderRadius,
          errorColor: `hsl(${style.getPropertyValue('--destructive')})`,
          inputColor: '#fff',
        },
      };

      teConnect.mountCardEntry(magensaContainerId, cardStyles);
      setTeConnectRef(teConnect);
    }
  }, [teConnectRef, authHeader]);

  useEffect(() => {
    mountCardIframe();

    return () => {
      document.getElementById(magensaContainerId)?.replaceChildren();
      setTeConnectRef(null);
    };
  }, []);

  const getCardType = (first4: string) => {
    if (!first4) return '';

    const cardInfo = creditCardType(first4);
    if (cardInfo && cardInfo.length > 0) {
      return cardInfo[0]?.niceType ?? '';
    }

    return '';
  };

  const clickHandler = async () => {
    if (!teConnectRef) return null;
    try {
      const teConnectResponse = await teConnectRef.createPayment();

      if (teConnectResponse) {
        const result = {
          token: teConnectResponse?.token,
          card_expiration_date: teConnectResponse?.cardMetaData.expirationDate,
          first6: teConnectResponse?.cardMetaData.maskedPAN.slice(0, 6),
          last4: teConnectResponse?.cardMetaData.maskedPAN.slice(-4),
          card_holder_name: '',
          cardType: getCardType(
            teConnectResponse?.cardMetaData.maskedPAN.slice(0, 4),
          ),
          card_zip_code: teConnectResponse?.cardMetaData?.billingZip,
        };
        return result;
      }
      return null;
    } catch (err) {
      return null;
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const teConnectResponse = await clickHandler();

    if (teConnectResponse?.token) {
      bambooTopUpTransactionMutation.mutate({
        input_obj: {
          balance_change: Number(balanceChange) ?? 0,
          date: formatDateToTimezone(
            new Date().toISOString(),
            user.time_zone!,
            `yyyy-MM-dd'T'HH:mm:ssXXX`,
          ),
          description: '',
          discount_percent_value: Number(
            bigDecimal.multiply(discountPercentValue, 100),
          ),
          gift_card_id: id ?? '',
          whmcs_id: user.whmcs_id ?? 0,
          payments: [
            {
              payment_method: paymentMethods.MANUAL_CARD_ENTRY,
              price: price?.toString()!,
              card_info: {
                card_expiration_date: formatVisaExpiry(
                  teConnectResponse.card_expiration_date,
                ),
                card_first_six: teConnectResponse?.first6,
                card_last_four: teConnectResponse?.last4,
                card_holder_name: teConnectResponse?.card_holder_name,
                card_type: teConnectResponse?.cardType,
                card_zip_code: teConnectResponse?.card_zip_code,
              },
              manual_card_info: {
                token: teConnectResponse?.token,
              },
            },
          ],
        },
      });
    }
  };

  if (bambooTopUpTransactionMutation.isSuccess) {
    return (
      <PaymentSuccess
        onBack={() =>
          navigate(
            `/gift-card/add-fund/${code}/signature?transaction_id=${bambooTopUpTransactionMutation.data.bamboo_top_up_transaction.id}&price=${price}`,
          )
        }
      />
    );
  }

  if (bambooTopUpTransactionMutation.isError) {
    return <PaymentFailed errors={error} onBack={() => navigate(-1)} />;
  }
  return (
    <TEConnect teConnect={teConnectInstance}>
      <div className="flex size-full items-center justify-center">
        <TopNavigation
          onBack={() => navigate(`/gift-card/add-fund/${code}?id=${id}`)}
        />
        <div className="w-full max-w-[500px]">
          <div id={'element_4_compute'} className="max-w-[500px]"></div>
          <Card className="p-8">
            <div className="flex items-center justify-between text-2xl font-bold">
              <div>Total Amount:</div>
              <div>{formatNumberToCurrency(price)}</div>
            </div>
          </Card>
          <div className="h-8 w-full" />
          <div>
            <div className="w-full max-w-[500px]">
              <div
                id={magensaContainerId}
                className={'h-[400px] max-w-[500px]'}
              />
            </div>
            <Button
              onClick={onSubmit}
              className="h-16 w-full text-2xl font-bold"
            >
              Pay
            </Button>
          </div>
        </div>
      </div>
    </TEConnect>
  );
}
